import { Link, useParams, useSearchParams } from 'react-router-dom'
import { ROUTE_PATHS } from 'core/constants/routePath'
import { useDebugRequeteDetailQuery } from 'iadocs/api/debug'
import { displayValue, getDebugErrorMessage } from 'iadocs/debug/display'
import { useDebugHeader } from 'iadocs/debug/search'
import { DebugRequeteCache } from 'iadocs/interfaces/IDebug'

const JsonBlock = ({
  value,
  isJson,
}: {
  value: string | null
  isJson: boolean
}) => {
  if (!value) {
    return <div className="debug-meta">Aucune donnée</div>
  }

  return (
    <>
      <div className="debug-meta debug-badge-row">
        <span className={`debug-badge ${isJson ? 'ok' : 'warn'}`}>
          {isJson ? 'JSON valide' : 'JSON invalide - valeur brute'}
        </span>
      </div>
      <pre className="debug-json">{value}</pre>
    </>
  )
}

const CacheBlock = ({ cache }: { cache: DebugRequeteCache }) => {
  return (
    <>
      <div className="debug-fields">
        <div className="debug-value-field">
          <div className="debug-label">ID cache</div>
          <div>{displayValue(cache.id)}</div>
        </div>
        <div className="debug-value-field">
          <div className="debug-label">Date création cache</div>
          <div>{displayValue(cache.dateCreation)}</div>
        </div>
        <div className="debug-value-field">
          <div className="debug-label">Date modification cache</div>
          <div>{displayValue(cache.dateModification)}</div>
        </div>
        <div className="debug-value-field">
          <div className="debug-label">Partenaire cache</div>
          <div>{displayValue(cache.partenaire)}</div>
        </div>
        <div className="debug-value-field">
          <div className="debug-label">Type document cache</div>
          <div>{displayValue(cache.typeDocument)}</div>
        </div>
        <div className="debug-value-field">
          <div className="debug-label">Document unique</div>
          <div>
            {cache.documentUnique === null
              ? '-'
              : cache.documentUnique
                ? 'Oui'
                : 'Non'}
          </div>
        </div>
        <div className="debug-value-field">
          <div className="debug-label">hash_document</div>
          <div>{displayValue(cache.hashDocument)}</div>
        </div>
        {'userInputHash' in cache && (
          <div className="debug-value-field">
            <div className="debug-label">user_input_hash</div>
            <div>{displayValue(cache.userInputHash)}</div>
          </div>
        )}
        {'apiVersion' in cache && (
          <div className="debug-value-field">
            <div className="debug-label">api_version</div>
            <div>{displayValue(cache.apiVersion)}</div>
          </div>
        )}
      </div>
      <div className="debug-label">réponse_partenaire (déchiffrée)</div>
      <JsonBlock
        value={cache.reponsePartenaire}
        isJson={cache.reponsePartenaireIsJson}
      />
    </>
  )
}

export const DebugRequeteDetailPage = () => {
  useDebugHeader()
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const requeteId = Number(id)
  const invalidId = !Number.isInteger(requeteId) || requeteId < 1
  const { data, isLoading, isError, error } = useDebugRequeteDetailQuery(
    requeteId,
    { skip: invalidId }
  )

  const backSearch = searchParams.toString()

  return (
    <div className="iadocs-debug">
      <div className="debug-actions">
        <Link
          className="debug-btn"
          to={{
            pathname: ROUTE_PATHS.IADOCS_DEBUG,
            search: backSearch,
          }}
        >
          Retour à la liste
        </Link>
      </div>

      {invalidId && (
        <div className="debug-flash error">
          Identifiant de requête invalide.
        </div>
      )}
      {isLoading && <div className="debug-empty">Chargement...</div>}
      {isError && (
        <div className="debug-flash error">{getDebugErrorMessage(error)}</div>
      )}
      {!isLoading && !isError && data && (
        <div className="debug-card">
          <div className="debug-header">
            <h1>Détail requête #{data.item.id}</h1>
            <div className="debug-meta">
              Créée le {displayValue(data.item.dateCreation)}
            </div>
          </div>
          <div className="debug-content">
            <div className="debug-fields">
              {(
                [
                  ['ID', data.item.id],
                  ['Date de modification', data.item.dateModification],
                  ['Partenaire', data.item.partenaire],
                  ['Type document', data.item.typeDocument],
                  ['Statut technique', data.item.statutTech],
                  ['Origine', data.item.origine],
                  ['ID dossier', data.item.idDossier],
                  ['ID dossier partenaire', data.item.idDossierPartenaire],
                  ['Client ID', data.item.clientId],
                ] as const
              ).map(([label, value]) => (
                <div className="debug-value-field" key={label}>
                  <div className="debug-label">{label}</div>
                  <div>{displayValue(value)}</div>
                </div>
              ))}
            </div>
            <div>
              <div className="debug-label">logReponse</div>
              <JsonBlock
                value={data.item.logReponse}
                isJson={data.item.logReponseIsJson}
              />
            </div>
            <div>
              <div className="debug-label">Cache associé</div>
              {data.cache ? (
                <CacheBlock cache={data.cache} />
              ) : (
                <div className="debug-meta">Aucun cache associé</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
